import { doc, getDoc, setDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import React, { useState, createContext, useEffect } from "react";
import { signOut, onAuthStateChanged, setPersistence, browserLocalPersistence } from "firebase/auth";
import { FleetRoles, normalizeFleetRole } from "../auth/fleetRoles";

const AuthContext = createContext();

function isInviteRoute() {
  if (typeof window === "undefined") return false;
  return window.location.pathname.startsWith("/accept-invite");
}

function isPermissionDenied(error) {
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  return code.includes("permission-denied") || message.includes("missing or insufficient permissions");
}

function AuthProviderWrapper(props) {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [fleetData, setFleetData] = useState(null);
  const [fleetId, setFleetId] = useState(null);
  const [fleetRole, setFleetRole] = useState(null);

  const storeToken = () => {
    localStorage.removeItem("authToken");
  };

  const removeToken = () => {
    localStorage.removeItem("authToken");
  };

  const storeFleetID = (fleetID) => {
    localStorage.setItem("fleetID", fleetID);
    setFleetId(fleetID);
  };

  const clearFleetId = () => {
    localStorage.removeItem("fleetID");
    setFleetId(null);
  };

  const getFleetID = () => {
    return localStorage.getItem("fleetID");
  };

  const clearSessionState = () => {
    setIsLoggedIn(false);
    setUser(null);
    setFleetData(null);
    setFleetRole(null);
    clearFleetId();
    removeToken();
  };

  const setInvitePendingState = (currentUser) => {
    setUser(currentUser);
    setIsLoggedIn(false);
    setFleetData(null);
    setFleetRole(null);
    clearFleetId();
    removeToken();
  };

  const getMemberRoleState = async ({ currentUser, activeFleetId }) => {
    try {
      const memberRef = doc(db, "fleets", activeFleetId, "members", currentUser.uid);
      const memberSnap = await getDoc(memberRef);
      if (!memberSnap.exists()) {
        return {
          hasMemberDoc: false,
          role: currentUser.uid === activeFleetId ? FleetRoles.OWNER : null,
        };
      }
      const memberData = memberSnap.data() || {};
      if (String(memberData.status || "").toUpperCase() !== "ACTIVE") {
        return {
          hasMemberDoc: true,
          role: null,
        };
      }
      return {
        hasMemberDoc: true,
        role: normalizeFleetRole(memberData.role),
      };
    } catch (error) {
      return {
        hasMemberDoc: false,
        role: null,
        permissionDenied: isPermissionDenied(error),
        error,
      };
    }
  };

  const resolveFleetCandidate = async ({ currentUser, candidateFleetId }) => {
    const memberState = await getMemberRoleState({
      currentUser,
      activeFleetId: candidateFleetId,
    });
    const isLegacyOwnerCandidate = currentUser.uid === candidateFleetId;

    if (memberState.error && !memberState.permissionDenied) {
      throw memberState.error;
    }

    if (memberState.permissionDenied && !isLegacyOwnerCandidate) {
      return null;
    }

    if (!memberState.role && !isLegacyOwnerCandidate) {
      return null;
    }

    const fleetSnap = await getDoc(doc(db, "fleets", candidateFleetId));
    if (!fleetSnap.exists()) {
      return null;
    }

    if (!memberState.hasMemberDoc && isLegacyOwnerCandidate) {
      await ensureLegacyOwnerMemberDocument({
        currentUser,
        activeFleetId: candidateFleetId,
        hasMemberDoc: memberState.hasMemberDoc,
      });
    }

    const role = memberState.role || (isLegacyOwnerCandidate ? FleetRoles.OWNER : null);
    if (!role) {
      return null;
    }

    return {
      fleetId: candidateFleetId,
      fleetData: { id: candidateFleetId, ...fleetSnap.data() },
      fleetRole: role,
    };
  };

  const ensureLegacyOwnerMemberDocument = async ({ currentUser, activeFleetId, hasMemberDoc }) => {
    if (currentUser.uid !== activeFleetId || hasMemberDoc) {
      return;
    }

    try {
      await setDoc(
        doc(db, "fleets", activeFleetId, "members", currentUser.uid),
        {
          userId: currentUser.uid,
          fleetId: activeFleetId,
          email: currentUser.email || null,
          displayName: currentUser.displayName || currentUser.email || null,
          role: FleetRoles.OWNER,
          status: "ACTIVE",
          invitedBy: currentUser.uid,
          updatedAt: new Date(),
          createdAt: new Date(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Could not ensure owner member document:", error);
    }
  };

  const resolveSessionForUser = async (
    currentUser,
    { forceRefresh = false, retryWithRefresh = true } = {}
  ) => {
    const tokenResult = await currentUser.getIdTokenResult(forceRefresh);
    const claimFleetId = String(tokenResult?.claims?.fleetId || "").trim();
    const storedFleetId = getFleetID();
    const fleetCandidates = Array.from(
      new Set([claimFleetId, storedFleetId, currentUser.uid].map((value) => String(value || "").trim()).filter(Boolean))
    );

    let resolvedSession = null;
    let lastError = null;

    for (const candidateFleetId of fleetCandidates) {
      try {
        resolvedSession = await resolveFleetCandidate({
          currentUser,
          candidateFleetId,
        });
        if (resolvedSession) break;
      } catch (error) {
        if (!isPermissionDenied(error)) {
          lastError = error;
        }
      }
    }

    if (!resolvedSession) {
      if (!forceRefresh && retryWithRefresh) {
        return resolveSessionForUser(currentUser, {
          forceRefresh: true,
          retryWithRefresh: false,
        });
      }
      if (lastError) {
        console.error("Could not resolve fleet candidate:", lastError);
      }
      throw new Error("No fleet profile found for current user");
    }

    return resolvedSession;
  };

  const authenticateUser = async ({ forceRefresh = false } = {}) => {
    setLoading(true);
    const currentUser = auth.currentUser;
    if (currentUser) {
      const requiresEmailVerification = Boolean(currentUser.email) && !currentUser.emailVerified;
      if (requiresEmailVerification) {
        if (isInviteRoute()) {
          setInvitePendingState(currentUser);
        } else {
          await signOut(auth);
          clearSessionState();
        }
        setLoading(false);
        return;
      }

      try {
        const session = await resolveSessionForUser(currentUser, { forceRefresh });

        if (String(session.fleetData?.status || "").toUpperCase() === "DELETED") {
          await signOut(auth);
          clearSessionState();
          setLoading(false);
          return;
        }

        setUser(currentUser);
        setIsLoggedIn(true);
        setFleetRole(session.fleetRole);
        setFleetData(session.fleetData);
        setFleetId(session.fleetId);
        storeFleetID(session.fleetId);
        setLoading(false);
        return session;
      } catch (error) {
        console.error("Error resolving user session:", error);
        if (isInviteRoute()) {
          setInvitePendingState(currentUser);
        } else {
          await signOut(auth);
          clearSessionState();
        }
      }

      setLoading(false);
      return null;
    } else {
      clearSessionState();
      setLoading(false);
      return null;
    }
  };

  const logOutUser = async () => {
    if (auth.currentUser) {
      await signOut(auth);
    }

    clearSessionState();
  };

  useEffect(() => {
    let unsubscribe = null;
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      console.error("Failed to set auth persistence:", error);
    });

    setLoading(true);
    unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const requiresEmailVerification = Boolean(currentUser.email) && !currentUser.emailVerified;
        if (requiresEmailVerification) {
          if (isInviteRoute()) {
            setInvitePendingState(currentUser);
          } else {
            await signOut(auth);
            clearSessionState();
          }
          setLoading(false);
          return;
        }

        try {
          const session = await resolveSessionForUser(currentUser);
          if (String(session.fleetData?.status || "").toUpperCase() === "DELETED") {
            await signOut(auth);
            clearSessionState();
            setLoading(false);
            return;
          }

          setUser(currentUser);
          setIsLoggedIn(true);
          setFleetRole(session.fleetRole);
          setFleetData(session.fleetData);
          setFleetId(session.fleetId);
          storeFleetID(session.fleetId);
        } catch (error) {
          console.error("Failed to resolve auth state:", error);
          if (isInviteRoute()) {
            setInvitePendingState(currentUser);
          } else {
            await signOut(auth);
            clearSessionState();
          }
        }
      } else {
        clearSessionState();
      }
      setLoading(false);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn,
        isLoading,
        storeToken,
        storeFleetID,
        getFleetID,
        authenticateUser,
        logOutUser,
        fleetId,
        fleetData,
        fleetRole,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
}

export { AuthProviderWrapper, AuthContext };
